import { memo, useRef } from 'react';

import { type GameAction, KEY_ROWS, mapScreenKey } from '../game/keyboard.ts';
import { switchLabel } from './ClueBar.tsx';
import { Icon } from './icons.tsx';

/**
 * The on-screen keyboard — the game's only keyboard on touch devices (no hidden input
 * summoning the OS keyboard). Keys act on pointer-down for snappy typing and keep
 * focus on the grid; keyboard activation (Enter/Space on a focused key) still works.
 */
export const Keyboard = memo(function Keyboard({
	onAction,
	disabled,
	axis,
}: {
	onAction: (a: GameAction) => void;
	/** The puzzle's done: the keys fade (and the foot replaces them). */
	disabled: boolean;
	/** The selected answer's direction, for the swap key's name. */
	axis?: 'across' | 'down' | null | undefined;
}) {
	// The key handled on pointer-down, so the click that follows doesn't type it twice.
	const pointerKey = useRef<string | null>(null);
	const release = (e: React.PointerEvent<HTMLButtonElement>) => e.currentTarget.classList.remove('down');
	const press = (key: string) => {
		const action = mapScreenKey(key);
		if (action) onAction(action);
	};
	return (
		<div className="kbd" role="group" aria-label="Keyboard" data-off={disabled ? '' : undefined}>
			{KEY_ROWS.map((row, r) => (
				<div className="krow" key={r}>
					{r === 1 && <span className="half" />}
					{row.map((key) => {
						const wide = key === 'SWAP' || key === 'BACK';
						const label = key === 'SWAP' ? switchLabel(axis) : key === 'BACK' ? 'Delete' : key;
						return (
							<button
								key={key}
								type="button"
								className={wide ? `key wide ${key === 'SWAP' ? 'swap' : 'backspace'}` : 'key'}
								aria-label={label}
								tabIndex={-1}
								onPointerDown={(e) => {
									if (e.button !== 0) return;
									e.preventDefault();
									pointerKey.current = key;
									// The physical press (raised → pressed) is a class, not React state, so a
									// fast typist never waits on a render for the key to go down.
									e.currentTarget.classList.add('down');
									press(key);
								}}
								onPointerUp={release}
								onPointerLeave={release}
								onPointerCancel={release}
								onClick={() => {
									// Pointer presses were handled on pointer-down; anything else (keyboard,
									// assistive tech) types here.
									if (pointerKey.current === key) pointerKey.current = null;
									else press(key);
								}}
							>
								{key === 'SWAP' ? <Icon.swap /> : key === 'BACK' ? <Icon.back /> : key}
							</button>
						);
					})}
					{r === 1 && <span className="half" />}
				</div>
			))}
		</div>
	);
});
